/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import API from '@apps/nnc/services/failuremode';
import ActionPlanAPI from '@apps/nnc/services/actionplan';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useFieldArray } from 'react-hook-form';
import {
  AutocompleteElement,
  FormContainer,
  TextFieldElement,
} from 'react-hook-form-mui';
import {
  FailModeFormType,
  FailModeSchemaType,
  FailModeFormSchema,
} from '@apps/nnc/schemas/failmode';

import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Stack,
  Tooltip,
  Modal,
  Chip,
  alpha,
  Autocomplete,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { Spinner, YesNoAutocompleteElement } from '@/components/atoms';
import { enqueueSnackbar } from 'notistack';
import {
  DataGrid,
  GridActionsCellItem,
  GridRenderCellParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import { DatePickerElement } from 'react-hook-form-mui/date-pickers';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useNotifications } from '@/contexts/notificationsCount';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DownloadIcon from '@mui/icons-material/Download';
import { downloadFileFromPath } from '@/utils/download';
import { formatDateSafely } from '@/utils/dateUtils';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { FilePicker } from '@/components/atoms/filepicker';
import FunctionalAreaAPI from '@apps/nnc/services/functionalarea';
import AsyncAutocompleteElement from '@/components/atoms/asyncautocomplete';
import { useAuth } from '@/contexts/auth';

const formatBody = (str: string | undefined): number[] => {
  if (!str) return [];
  return str.includes('-')
    ? str.split('-').map((i) => Number(i))
    : [Number(str)];
};

const NNCAnalysisForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { fetchNotifications } = useNotifications();
  const [open, setOpen] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] =
    React.useState<GridRowSelectionModel>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [newAction, setNewAction] = useState<any>({
    description: '',
    status: { label: t('On Going'), id: 2 },
    due_date: undefined,
    area: '',
    responsible: '',
    implemented: undefined,
    displayed: false,
    functional_area: undefined,
  });
  const { area_leader } = useAuth();
  const [modalData, setModalData] = useState<any>();
  const [functionalAreas, setFunctionalAreas] = useState<any[]>([]);
  const [successReferenceNumber, setSuccessReferenceNumber] = useState<
    string | null
  >(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const getFunctionalAreas = async () => {
    try {
      const response = await FunctionalAreaAPI.select({});
      setFunctionalAreas(response?.data as any);
    } catch (error) {
      console.error('Error fetching functional areas:', error);
    }
  };

  const handleOpenModal = async () => {
    const response = await API.list({
      status: 1,
      failure_id: id
    });
    setModalData(response.data.results);
    setOpen(true);
  };

  const columns = [
    {
      field: 'division',
      headerName: t('Identified Area'),
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const machines = params.row.other_divisions || [];
        return (
          <Box
            display={'flex'}
            alignItems={'center'}
            gap={1}
            sx={{ height: '100%' }}
          >
            {params.row.main_division || ''}
            {machines.length > 0 && (
              <Tooltip
                title={
                  <>
                    {machines.map((machine: string, index: number) => (
                      <Box key={index} sx={{ marginBottom: '4px' }}>
                        {machine}
                      </Box>
                    ))}
                  </>
                }
              >
                <Box
                  sx={{
                    background: '#00d160',
                    padding: '0 5px',
                    borderRadius: '4px',
                    color: '#fff',
                    minWidth: '30px',
                    display: 'flex',
                    height: '30px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  +{machines.length || 0}
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'machine',
      headerName: t('NNC Machine'),
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const machines = params.row.other_machine || [];
        return (
          <Box
            display={'flex'}
            alignItems={'center'}
            gap={1}
            sx={{ height: '100%' }}
          >
            {params.row.main_machine || ''}
            {machines.length > 0 && (
              <Tooltip
                title={
                  <>
                    {machines.map((machine: string, index: number) => (
                      <Box key={index} sx={{ marginBottom: '4px' }}>
                        {machine}
                      </Box>
                    ))}
                  </>
                }
              >
                <Box
                  sx={{
                    background: '#00d160',
                    padding: '0 5px',
                    borderRadius: '4px',
                    color: '#fff',
                    minWidth: '30px',
                    display: 'flex',
                    height: '30px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  +{machines.length || 0}
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'operator',
      headerName: t('Operator Generator'),
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const machines = params.row.other_operators || [];
        return (
          <Box
            display={'flex'}
            alignItems={'center'}
            gap={1}
            sx={{ height: '100%' }}
          >
            {params.row.main_operator || ''}
            {machines.length > 0 && (
              <Tooltip
                title={
                  <>
                    {machines.map((machine: string, index: number) => (
                      <Box key={index} sx={{ marginBottom: '4px' }}>
                        {machine}
                      </Box>
                    ))}
                  </>
                }
              >
                <Box
                  sx={{
                    background: '#00d160',
                    padding: '0 5px',
                    borderRadius: '4px',
                    color: '#fff',
                    minWidth: '30px',
                    display: 'flex',
                    height: '30px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  +{machines.length || 0}
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'origin',
      headerName: t('Origin'),
      renderCell: (params: GridRenderCellParams) => {
        return params?.row?.origin?.label;
      },
      flex: 1,
    },
    {
      field: 'cause',
      headerName: t('Cause'),
      renderCell: (params: GridRenderCellParams) => {
        return params?.row?.cause?.label;
      },
      flex: 1,
    },
    {
      field: 'comment',
      headerName: t('Comment'),
      flex: 1,
    },
  ];

  const { data, isLoading, error } = useQuery<
    { data: FailModeSchemaType[] },
    Error
  >({
    queryKey: ['entries', rowSelectionModel],
    queryFn: () => API.detail('entries', { ids: [...rowSelectionModel] }),
  });

  const form = useForm<FailModeFormType>({
    resolver: zodResolver(FailModeFormSchema),
    defaultValues: {
      comment_general: '',
      actions: [],
    },
  });

  const { fields, prepend, remove, update } = useFieldArray({
    control: form.control,
    name: 'actions',
  });

  const handleAddCause = () => {
    setShowAddForm(true);
    setNewAction({
      description: '',
      status: { label: t('On Going'), id: 2 },
      due_date: undefined,
      area: '',
      responsible: '',
      implemented: undefined,
      displayed: false,
      functional_area: undefined,
    });
  };

  const handleSaveNewAction = () => {
    const actionToSave = {
      ...newAction,
      displayed: true,
      due_date: newAction.due_date
        ? dayjs(newAction.due_date).toDate()
        : undefined,
    };

    if (editIndex !== null) {
      // Para edição, usar update do useFieldArray
      update(editIndex, {
        ...actionToSave,
        id: newAction.actionId,
      });
      setEditIndex(null);
    } else {
      // Para nova ação, adicionar no início
      prepend(actionToSave);
    }

    setShowAddForm(false);
    setNewAction({
      description: '',
      status: { label: t('On Going'), id: 2 },
      due_date: undefined,
      area: '',
      responsible: '',
      implemented: undefined,
      displayed: false,
      functional_area: undefined,
    });
  };

  const isNewActionValid = () => {
    const needsImplemented =
      newAction.status?.id === 3 &&
      area_leader?.includes(newAction.functional_area?.id);
    return (
      newAction.description?.trim() &&
      newAction.status &&
      newAction.functional_area &&
      newAction.responsible?.trim() &&
      (!needsImplemented || newAction.implemented !== undefined)
    );
  };

  const isActionValid = (index: number) => {
    const watchedValues = form.watch(`actions.${index}`);
    const needsImplemented =
      watchedValues?.status?.id === 3 &&
      watchedValues?.functional_area?.id &&
      area_leader?.includes(watchedValues.functional_area.id);
    return (
      watchedValues?.description &&
      watchedValues?.status &&
      watchedValues?.functional_area &&
      watchedValues?.responsible &&
      (!needsImplemented || watchedValues?.implemented !== undefined)
    );
  };

  const handleContinue = () => {
    navigate('/nnc/failuremode');
  };

  const handleSubmit = async (data: FailModeFormType) => {
    try {
      setLoading(true);

      const actionsFormatted =
        data?.actions
          ?.filter((i) => i.displayed == true)
          .map((action) => ({
            ...action,
            due_date: formatDateSafely(action.due_date) || undefined,
          })) || [];

      const response = await ActionPlanAPI.createActionPlan({
        failure_ids: rowSelectionModel,
        ...data,
        actions: actionsFormatted,
      });

      setSuccessReferenceNumber(response.data.reference_number);
      setShowSuccessMessage(true);
      setLoading(false);

      enqueueSnackbar(t('Action Plan created successfully!'), {
        variant: 'success',
      });
      fetchNotifications();
    } catch (e) {
      console.error(e);
      setLoading(false);
      enqueueSnackbar(t('Error when trying to create Action Plan'), {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    if (Object.keys(form.formState.errors).length > 0) {
      console.log('Zod validation errors:', form.formState.errors);
      console.log('ZOD DATA: ', form.getValues());
    }
  }, [form.formState.errors, form]);

  useEffect(() => {
    const ids = formatBody(id);
    setRowSelectionModel(ids);
  }, [id]);

  useEffect(() => {
    getFunctionalAreas();
  }, []);

  useEffect(() => {
    console.log('Area Leader:', area_leader);
  }, [area_leader]);

  if (error) {
    return (
      <Box>
        <Typography color="error">
          Erro ao carregar dados: {(error as Error).message}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 2, minHeight: '100vh' }}>
        <Spinner open={isLoading || loading} />

        {showSuccessMessage && successReferenceNumber && (
          <Card
            elevation={4}
            sx={{ borderRadius: 4, mb: 3, textAlign: 'center' }}
          >
            <CardContent>
              <Box display="flex" justifyContent="center" mb={2}>
                <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main' }} />
              </Box>
              <Typography variant="h5" color="success.main" gutterBottom>
                {t('Action Plan created successfully!')}
              </Typography>
              <Typography variant="h6" sx={{ mt: 2, mb: 3 }}>
                {t('Reference Number')}:{' '}
                <strong>{successReferenceNumber}</strong>
              </Typography>
              <Button
                variant="gradient"
                color="primary"
                size="large"
                onClick={handleContinue}
                sx={{ mt: 2 }}
              >
                {t('Continue')}
              </Button>
            </CardContent>
          </Card>
        )}

        {!showSuccessMessage && (
          <FormContainer formContext={form} onSuccess={handleSubmit}>
            <Box mb={3} display={'flex'} justifyContent={'flex-end'}>
              <Button
                onClick={() => {
                  handleOpenModal();
                }}
              >
                + {t('Include Another Cause')}
              </Button>
            </Box>
            <Stack spacing={2}>
              <Card elevation={4} sx={{ borderRadius: 4 }}>
                <CardContent>
                  <TextFieldElement
                    name={`theme`}
                    fullWidth
                    label={t('Action Plan Theme')}
                    required
                    sx={{
                      mb: 3,
                    }}
                  />
                  <DataGrid
                    rows={data?.data || []}
                    columns={[
                      ...columns,
                      {
                        field: 'actions',
                        width: 50,
                        headerName: '',
                        renderCell: (params: GridRenderCellParams) => {
                          return (
                            <GridActionsCellItem
                              sx={{
                                background: '#2D354A',
                                color: '#0093ff',
                                borderRadius: '16px',
                              }}
                              key={`edit-${params?.row.id}`}
                              icon={<DeleteOutlineIcon />}
                              onClick={() => {
                                setRowSelectionModel((prev) =>
                                  prev.filter((id) => id !== params.row.id),
                                );
                              }}
                              label="remove"
                            />
                          );
                        },
                      },
                    ]}
                    getRowId={(row) => row.id.toString()}
                    hideFooter
                  />
                </CardContent>
              </Card>

              {/* General Comment */}
              <Card elevation={4} sx={{ borderRadius: 4 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    {t('General Comment')}
                  </Typography>
                  <TextFieldElement
                    name="comment_general"
                    fullWidth
                    multiline
                    rows={3}
                  />
                </CardContent>
              </Card>

              <Card elevation={4} sx={{ borderRadius: 4 }}>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    pt={1}
                  >
                    <Typography fontWeight={18} variant="subtitle1">
                      {t('Create Actions')}
                    </Typography>
                    {!showAddForm && (
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddCause}
                        color="secondary"
                      >
                        {t('Add Action')}
                      </Button>
                    )}
                  </Box>

                  {showAddForm && (
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                      <Grid size={{ xs: 12, md: 7 }}>
                        <TextField
                          name="newAction.description"
                          label={t('Action Description')}
                          fullWidth
                          value={newAction.description}
                          onChange={(e) =>
                            setNewAction((a: any) => ({
                              ...a,
                              description: e.target.value,
                            }))
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <Autocomplete
                          value={newAction.status}
                          onChange={(_, newValue) => {
                            setNewAction((a: any) => ({
                              ...a,
                              status: newValue,
                              // Limpa o implemented se não for "Completed"
                              implemented:
                                newValue?.id === 3 ? a.implemented : undefined,
                            }));
                          }}
                          options={[
                            { label: t('On Going'), id: 2 },
                            { label: t('Completed'), id: 3 },
                          ]}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              name="newAction.status"
                              label={t('Status')}
                              fullWidth
                            />
                          )}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 2 }}>
                        <DatePicker
                          name="newAction.due_date"
                          label={t('Due Date')}
                          value={newAction.due_date}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                            },
                          }}
                          onChange={(value) =>
                            setNewAction((a: any) => ({
                              ...a,
                              due_date: value ? dayjs(value) : null,
                            }))
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <Autocomplete
                          value={newAction.functional_area}
                          onChange={(_, newValue) => {
                            setNewAction((a: any) => {
                              const shouldClearImplemented = !(
                                a.status?.id === 3 &&
                                newValue?.id &&
                                area_leader?.includes(newValue.id)
                              );
                              return {
                                ...a,
                                functional_area: newValue,
                                // Limpa o implemented se não atender às condições
                                implemented: shouldClearImplemented
                                  ? undefined
                                  : a.implemented,
                              };
                            });
                          }}
                          options={functionalAreas}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              name="newAction.functional_area"
                              label={t('Functional Area')}
                              fullWidth
                            />
                          )}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <TextField
                          name="newAction.responsible"
                          label={t('Responsible')}
                          fullWidth
                          value={newAction.responsible}
                          onChange={(e) =>
                            setNewAction((a: any) => ({
                              ...a,
                              responsible: e.target.value,
                            }))
                          }
                        />
                      </Grid>
                      {newAction.status?.id === 3 &&
                        area_leader?.includes(
                          newAction.functional_area?.id,
                        ) && (
                          <Grid size={{ xs: 12, md: 2 }}>
                            <Autocomplete
                              value={newAction.implemented}
                              onChange={(_, newValue) =>
                                setNewAction((a: any) => ({
                                  ...a,
                                  implemented: newValue,
                                }))
                              }
                              options={[
                                { label: t('Yes'), id: 'true' },
                                { label: t('No'), id: 'false' },
                              ]}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  name="newAction.implemented"
                                  label={t('Implemented?')}
                                  fullWidth
                                />
                              )}
                            />
                          </Grid>
                        )}
                      <Grid
                        size={{ xs: 12, md: 2 }}
                        display={'flex'}
                        alignItems="center"
                      >
                        <FilePicker
                          label={t('Evidency')}
                          value={newAction.file}
                          onChange={(file) =>
                            setNewAction((a: any) => ({
                              ...a,
                              file: file,
                            }))
                          }
                          sx={{ height: '100%', fontSize: '18px' }}
                          color="primary"
                        />
                      </Grid>
                      <Grid
                        size={{ xs: 12, md: 2 }}
                        display={'flex'}
                        alignItems="center"
                      >
                        <Button
                          fullWidth
                          sx={{ height: '100%', fontSize: '18px' }}
                          onClick={handleSaveNewAction}
                          variant="contained"
                          color="secondary"
                          disabled={!isNewActionValid()}
                        >
                          {editIndex !== null
                            ? t('Save Edit')
                            : t('Save Action')}
                        </Button>
                      </Grid>
                    </Grid>
                  )}

                  {fields
                    .filter((i) => i.displayed == false)
                    .map((field, index) => (
                      <Grid
                        container
                        spacing={2}
                        key={field.id}
                        sx={{
                          mt: 2,
                          borderTop: index > 0 ? '1px solid #ccc' : 'none',
                          pt: index > 0 ? 2 : 0,
                        }}
                      >
                        <Grid size={{ xs: 12, md: 7 }}>
                          <TextFieldElement
                            name={`actions.${index}.description`}
                            fullWidth
                            label={t('Action Description')}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                          <AutocompleteElement
                            name={`actions.${index}.status`}
                            label={t('Status')}
                            options={[
                              { label: t('On Going'), id: 2 },
                              { label: t('Completed'), id: 3 },
                            ]}
                            required
                          />
                        </Grid>

                        <Grid size={{ xs: 12, md: 2 }}>
                          <DatePickerElement
                            name={`actions.${index}.due_date`}
                            label={t('Due Date')}
                            inputProps={{ sx: { width: '100%' } }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                          <AsyncAutocompleteElement
                            name={`actions.${index}.functional_area`}
                            label={t('Functional Area')}
                            api={FunctionalAreaAPI.select}
                            required
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                          <TextFieldElement
                            name={`actions.${index}.responsible`}
                            fullWidth
                            label={t('Responsible')}
                          />
                        </Grid>
                        {(() => {
                          const watchedStatus = form.watch(
                            `actions.${index}.status`,
                          );
                          const watchedFunctionalArea = form.watch(
                            `actions.${index}.functional_area`,
                          );
                          return (
                            watchedStatus?.id === 3 &&
                            watchedFunctionalArea?.id &&
                            area_leader?.includes(watchedFunctionalArea.id)
                          );
                        })() && (
                          <Grid size={{ xs: 12, md: 2 }}>
                            <YesNoAutocompleteElement
                              name={`actions.${index}.implemented`}
                              label={t('Implemented?')}
                            />
                          </Grid>
                        )}

                        <Grid
                          size={{ xs: 12, md: 2 }}
                          display={'flex'}
                          alignItems="center"
                        >
                          <FilePicker
                            name={`actions.${index}.file`}
                            label={t('Evidency')}
                            control={form.control}
                            sx={{ height: '100%', fontSize: '18px' }}
                            color="primary"
                          />
                        </Grid>
                        <Grid
                          size={{ xs: 12, md: 2 }}
                          display={'flex'}
                          alignItems="center"
                        >
                          <Button
                            fullWidth
                            sx={{ height: '100%', fontSize: '18px' }}
                            onClick={() => {
                              form.setValue(`actions.${index}.displayed`, true);
                              handleAddCause();
                            }}
                            variant="contained"
                            color="secondary"
                            disabled={!isActionValid(index)}
                          >
                            {t('Save Action')}
                          </Button>
                        </Grid>
                      </Grid>
                    ))}
                  {fields.filter((i) => i.displayed == true).length > 0 && (
                    <Box mt={4}>
                      <DataGrid
                        rows={fields
                          .filter((i) => i.displayed == true)
                          .map((field, idx) => ({
                            ...field,
                            id: idx, // DataGrid precisa de um id único
                            index: idx,
                            statusLabel: field.status?.label || '',
                            due_date: formatDateSafely(field.due_date) || '',
                            implementedLabel:
                              typeof field.implemented === 'object'
                                ? field.implemented?.label
                                : field.implemented === true
                                  ? t('Yes')
                                  : field.implemented === false
                                    ? t('No')
                                    : '',
                          }))}
                        columns={[
                          {
                            field: 'description',
                            headerName: t('Action Description'),
                            flex: 2,
                          },
                          {
                            field: 'functional_area',
                            headerName: t('Functional Area'),
                            flex: 1,
                            renderCell: (params: GridRenderCellParams) => {
                              return (
                                <Box
                                  display={'flex'}
                                  alignItems="center"
                                  gap={1}
                                >
                                  {params.row.functional_area?.label || ''}
                                </Box>
                              );
                            },
                          },
                          {
                            field: 'responsible',
                            headerName: t('Responsible'),
                            flex: 1,
                          },
                          ...(fields.some(
                            (field) =>
                              field.status?.id === 3 &&
                              field.functional_area?.id &&
                              area_leader?.includes(field.functional_area.id),
                          )
                            ? [
                                {
                                  field: 'implementedLabel',
                                  headerName: t('Implemented?'),
                                  flex: 1,
                                  renderCell: (
                                    params: GridRenderCellParams,
                                  ) => {
                                    // Só mostra se o status for "Completed" (id: 3) E a functional area estiver no area_leader
                                    const shouldShow =
                                      params.row.status?.id === 3 &&
                                      params.row.functional_area?.id &&
                                      area_leader?.includes(
                                        params.row.functional_area.id,
                                      );
                                    if (!shouldShow) {
                                      return null;
                                    }
                                    return (
                                      <Chip
                                        label={params.row.implementedLabel}
                                        color={
                                          params.row.implementedLabel ===
                                          t('Yes')
                                            ? 'success'
                                            : params.row.implementedLabel ===
                                                t('No')
                                              ? 'error'
                                              : 'default'
                                        }
                                        sx={{
                                          backgroundColor: (theme) =>
                                            params.row.implementedLabel ===
                                            t('Yes')
                                              ? alpha(
                                                  theme.palette.success.main,
                                                  0.2,
                                                )
                                              : params.row.implementedLabel ===
                                                  t('No')
                                                ? alpha(
                                                    theme.palette.error.main,
                                                    0.2,
                                                  )
                                                : alpha(
                                                    theme.palette.grey[400],
                                                    0.2,
                                                  ),
                                          color: (theme) =>
                                            params.row.implementedLabel ===
                                            t('Yes')
                                              ? theme.palette.success.main
                                              : params.row.implementedLabel ===
                                                  t('No')
                                                ? theme.palette.error.main
                                                : theme.palette.text.primary,
                                        }}
                                      />
                                    );
                                  },
                                },
                              ]
                            : []),

                          {
                            field: 'due_date',
                            headerName: t('Due Date'),
                            flex: 1,
                            renderCell: (params: GridRenderCellParams) => {
                              const value = params.row.due_date;
                              let formatted = '';

                              if (value) {
                                // Se já é uma string formatada (YYYY-MM-DD), converte para DD/MM/YYYY
                                if (
                                  typeof value === 'string' &&
                                  /^\d{4}-\d{2}-\d{2}$/.test(value)
                                ) {
                                  const date = dayjs(value);
                                  formatted = date.isValid()
                                    ? date.format('DD/MM/YYYY')
                                    : '';
                                } else {
                                  // Para outros formatos, usa dayjs
                                  const date = dayjs(value);
                                  formatted = date.isValid()
                                    ? date.format('DD/MM/YYYY')
                                    : '';
                                }
                              }

                              return (
                                <Box display="flex" alignItems="center" gap={1}>
                                  <span>{formatted}</span>
                                  {formatted && (
                                    <CalendarTodayIcon
                                      color="primary"
                                      fontSize="small"
                                    />
                                  )}
                                </Box>
                              );
                            },
                          },
                          {
                            field: 'statusLabel',
                            headerName: t('Status'),
                            flex: 1,
                            renderCell: (params: GridRenderCellParams) => {
                              return (
                                <Chip
                                  label={params.row.statusLabel}
                                  color={
                                    params.row.statusLabel === t('Completed')
                                      ? 'success'
                                      : params.row.statusLabel === t('On Going')
                                        ? 'warning'
                                        : 'default'
                                  }
                                  sx={{
                                    backgroundColor: (theme) =>
                                      params.row.statusLabel === t('Completed')
                                        ? alpha(theme.palette.success.main, 0.2)
                                        : params.row.statusLabel ===
                                            t('On Going')
                                          ? alpha(
                                              theme.palette.warning.main,
                                              0.2,
                                            )
                                          : alpha(theme.palette.grey[400], 0.2),
                                    color: (theme) =>
                                      params.row.statusLabel === t('Completed')
                                        ? theme.palette.success.main
                                        : params.row.statusLabel ===
                                            t('On Going')
                                          ? theme.palette.warning.main
                                          : theme.palette.text.primary,
                                  }}
                                />
                              );
                            },
                          },
                          {
                            field: 'file',
                            headerName: t('Evidency'),
                            width: 90,
                            renderCell: (params: GridRenderCellParams) => {
                              const file = params.row.file;
                              const isPath =
                                typeof file === 'string' &&
                                file &&
                                !file.startsWith('data:');
                              const isBase64 =
                                typeof file === 'string' &&
                                file &&
                                file.startsWith('data:');
                              return (
                                <Box
                                  display={'flex'}
                                  alignItems="center"
                                  justifyContent={'center'}
                                  sx={{ height: '100%' }}
                                >
                                  {isPath && (
                                    <Tooltip title={file} placement="top">
                                      <GridActionsCellItem
                                        icon={<DownloadIcon color="primary" />}
                                        label="download"
                                        onClick={() =>
                                          downloadFileFromPath(file)
                                        }
                                      />
                                    </Tooltip>
                                  )}
                                  {isBase64 && (
                                    <Tooltip
                                      title={t('File not uploaded yet')}
                                      placement="top"
                                    >
                                      <UploadFileIcon color="primary" />
                                    </Tooltip>
                                  )}
                                </Box>
                              );
                            },
                          },
                          {
                            field: 'actions',
                            headerName: '',
                            width: 90,
                            renderCell: (params: GridRenderCellParams) => {
                              return (
                                <Box
                                  display="flex"
                                  gap={1}
                                  sx={{ height: '100%' }}
                                  alignItems={'center'}
                                  justifyContent={'center'}
                                >
                                  <GridActionsCellItem
                                    icon={<DeleteOutlineIcon />}
                                    label="remove"
                                    onClick={() => {
                                      remove(params.row.index);
                                    }}
                                    sx={{
                                      background: '#2D354A',
                                      color: '#0093ff',
                                      borderRadius: '16px',
                                    }}
                                  />
                                  <GridActionsCellItem
                                    icon={<EditIcon />}
                                    label="edit"
                                    onClick={() => {
                                      setEditIndex(params.row.index);
                                      const fieldData =
                                        fields[params.row.index];
                                      setNewAction({
                                        ...fieldData,
                                        due_date: fieldData.due_date
                                          ? dayjs(fieldData.due_date)
                                          : null,
                                        actionId: fieldData.id,
                                      });
                                      setShowAddForm(true);
                                    }}
                                    sx={{
                                      background: '#2D354A',
                                      color: '#ff9800',
                                      borderRadius: '16px',
                                    }}
                                  />
                                </Box>
                              );
                            },
                          },
                        ]}
                        hideFooter
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>

              <Box display="flex" justifyContent="flex-end" mt={2} gap={3}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/nnc/failuremode')}
                  sx={{ border: '1px solid #0093FF !important', color: '#000' }}
                >
                  {t('Cancel')}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={showAddForm}
                >
                  {t('Confirm')}
                </Button>
              </Box>
            </Stack>
          </FormContainer>
        )}
      </Box>

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
        }}
      >
        <Box
          sx={{
            width: '70vw',
            bgcolor: 'background.paper',
            p: 4,
            m: 'auto',
            mt: 10,
            maxHeight: '80vh',
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" component="h2" mb={4}>
            {t('Select the Cause you want to add')}
          </Typography>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', maxHeight: '50vh' }}
          >
            <DataGrid
              columns={columns}
              rows={modalData || []}
              checkboxSelection
              onRowSelectionModelChange={(newRowSelectionModel) => {
                setRowSelectionModel(newRowSelectionModel);
              }}
              rowSelectionModel={rowSelectionModel}
              hideFooter
              sx={{
                '& .MuiDataGrid-columnHeaders > div': {
                  backgroundColor: '#FCFCFD !important',
                },
              }}
            />
          </Box>
          <Box display={'flex'} justifyContent="flex-end" mt={2}>
            <Button
              onClick={() => {
                setOpen(false);
              }}
              sx={{ mt: 2 }}
            >
              {t('Close')}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default NNCAnalysisForm;
